import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import type { PhotoChecklist, PhotoDecision } from '../api/quality.js';
import { decidePhoto, listPhotos } from '../api/quality.js';
import { ApiError } from '../api/client.js';

const CHECKLIST_KEYS = [
  'colorMatch',
  'finishQuality',
  'edgeCoverage',
  'uniformity',
  'noOverspray',
  'noDrip',
  'noBubble',
  'noStreak',
  'surfacePrep',
] as const;

export function PhotoValidationPage(): ReactNode {
  const { id = '' } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [photos, setPhotos] = useState<readonly unknown[]>([]);
  const [photoId, setPhotoId] = useState('');
  const [checklist, setChecklist] = useState<PhotoChecklist>(() =>
    Object.fromEntries(CHECKLIST_KEYS.map((k) => [k, false])),
  );
  const [decision, setDecision] = useState<PhotoDecision>('APPROVED');
  const [reasonCode, setReasonCode] = useState('PHOTO_BLUR');
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setPhotos(await listPhotos(id, token)); } catch (err) { setError(err instanceof ApiError ? err.message : 'Load failed'); }
  }, [id, token]);
  useEffect(() => { void load(); }, [load]);

  async function submit(): Promise<void> {
    setError(null); setMsg(null); setBusy(true);
    try {
      await decidePhoto(id, photoId, checklist, decision, decision === 'REJECTED' ? reasonCode : undefined, decision === 'REJECTED' ? remarks : undefined, token);
      setMsg('Saved');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally { setBusy(false); }
  }

  return (
    <main aria-label="photo-validation">
      <h1>Photo validation — {id}</h1>
      {error ? <div role="alert">{error}</div> : null}
      {msg ? <div role="status">{msg}</div> : null}
      <section aria-label="photo-list">
        <div>{photos.length} photo(s)</div>
      </section>
      <form aria-label="photo-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <label>Photo ID
          <input aria-label="photo-id" value={photoId} onChange={(e) => setPhotoId(e.target.value)} required />
        </label>
        <fieldset aria-label="checklist">
          <legend>9-point checklist</legend>
          {CHECKLIST_KEYS.map((k) => (
            <label key={k}>
              <input
                type="checkbox"
                aria-label={`chk-${k}`}
                checked={Boolean(checklist[k])}
                onChange={(e) => setChecklist((c) => ({ ...c, [k]: e.target.checked }))}
              />
              {k}
            </label>
          ))}
        </fieldset>
        <label>Decision
          <select aria-label="decision" value={decision} onChange={(e) => setDecision(e.target.value as PhotoDecision)}>
            <option value="APPROVED">APPROVED</option>
            <option value="REJECTED">REJECTED</option>
            <option value="NEEDS_REVIEW">NEEDS_REVIEW</option>
          </select>
        </label>
        {decision === 'REJECTED' ? (
          <>
            <label>Reason
              <select aria-label="photo-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                <option value="PHOTO_BLUR">PHOTO_BLUR</option>
                <option value="WRONG_ANGLE">WRONG_ANGLE</option>
                <option value="POOR_LIGHT">POOR_LIGHT</option>
                <option value="QUALITY_FAIL">QUALITY_FAIL</option>
              </select>
            </label>
            <label>Remarks
              <textarea aria-label="photo-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} required />
            </label>
          </>
        ) : null}
        <button type="submit" disabled={busy}>Save</button>
      </form>
    </main>
  );
}
